/**
 * Buyer / org URL slug — shared by the API (which computes it once from the
 * registry name), the web canonical, and the prerender/sitemap, so all three
 * agree on the exact string. INN-FIRST: `{tin}-{name-slug}`.
 *
 * WHY INN-first (not name-first like the tender slug). The ИНН is the buyer's
 * stable public key and is always a valid bare URL on its own, so `/buyer/{tin}`
 * (every URL already indexed) stays a clean prefix of the slugged form and keeps
 * resolving — {@link extractOrgTin} recovers the ИНН from the leading digits with
 * no dependence on the name. The name is decoration for the crawler and the
 * click; the ИНН is the identity.
 *
 * The name half is derived from the REGISTRY name only (not the source-fallback
 * display name), because that is the one value the profile endpoint and the
 * `/buyers/index` list resolve identically — decoupling the slug from the
 * fallback is what guarantees the canonical, the sitemap entry and the
 * prerendered file are byte-identical URLs. A buyer with no registry name gets
 * the bare `{tin}` (exactly its URL today — no migration for that subset).
 */

import type { Country } from './country/types.js';

// Cyrillic (Russian + Uzbek) → Latin, for a readable ASCII slug. Multi-char
// first so `щ`/`ю` etc. resolve before the single-letter pass.
const TRANSLIT: ReadonlyArray<readonly [string, string]> = [
  ['щ', 'shch'],
  ['ю', 'yu'],
  ['я', 'ya'],
  ['ж', 'zh'],
  ['ч', 'ch'],
  ['ш', 'sh'],
  ['ё', 'yo'],
  ['ъ', ''],
  ['ь', ''],
  ['ў', "o'"],
  ['қ', 'q'],
  ['ғ', "g'"],
  ['ҳ', 'h'],
  ['а', 'a'],
  ['б', 'b'],
  ['в', 'v'],
  ['г', 'g'],
  ['д', 'd'],
  ['е', 'e'],
  ['з', 'z'],
  ['и', 'i'],
  ['й', 'y'],
  ['к', 'k'],
  ['л', 'l'],
  ['м', 'm'],
  ['н', 'n'],
  ['о', 'o'],
  ['п', 'p'],
  ['р', 'r'],
  ['с', 's'],
  ['т', 't'],
  ['у', 'u'],
  ['ф', 'f'],
  ['х', 'x'],
  ['ц', 'ts'],
  ['ы', 'i'],
  ['э', 'e'],
];

// Legal-form words dropped from the slug — they carry no keyword value and,
// leading, would eat the whole length cap («общество с ограниченной…»). Matched
// as whole tokens after transliteration, so both Cyrillic-origin and native
// Latin spellings are covered. Kept lowercase, apostrophes stripped.
const STOPWORDS = new Set([
  // ru abbreviations + transliterated phrase words
  'ooo',
  'oao',
  'zao',
  'ao',
  'gup',
  'dup',
  'up',
  'gp',
  'chp',
  'sp',
  'fh',
  'obshchestvo',
  's',
  'ogranichennoy',
  'ogranichennoi',
  'otvetstvennostyu',
  'aktsionernoe',
  'aksionernoe',
  'otkrytoe',
  'zakrytoe',
  'gosudarstvennoe',
  'unitarnoe',
  'predpriyatie',
  'chastnoe',
  'sovmestnoe',
  'semeynoe',
  // uz Latin
  'mchj',
  'aj',
  'oaj',
  'duk',
  'xk',
  'ok',
  'yatt',
  'masuliyati',
  'cheklangan',
  'jamiyati',
  'jamiyat',
  'aksiyadorlik',
  'ochiq',
  'yopiq',
  'davlat',
  'unitar',
  'korxonasi',
  'korxona',
  'xususiy',
  'oilaviy',
  'fermer',
  'xojaligi',
  'qoshma',
]);

const MAX_SLUG_LEN = 60;

/** Combining diacritics, as `String.prototype.normalize('NFD')` splits them off. */
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/** Deployments whose org names are written in a Latin script with accents, and
 *  whose slugs therefore fold them. Spain and Chile: a UZ/KZ slug is already
 *  indexed and must not move. */
function foldsAccents(country: Country | undefined): boolean {
  return country === 'es' || country === 'cl';
}

function transliterate(input: string, foldAccents = false): string {
  let s = input.toLowerCase();
  // Fold apostrophe variants so `o'zbek` → `ozbek` (the apostrophe is dropped by
  // slugification anyway; folding first keeps `o'` and `oʻ` identical).
  s = s.replace(/[ʻʼ'’`´]/g, '');
  for (const [from, to] of TRANSLIT) s = s.split(from).join(to);
  // Spanish (and Catalan, Galician) names carry accents, ñ, ç and ü —
  // «Transformación», «Alcaldía». Unfolded, the `[^a-z0-9]` pass in
  // slugifyOrgName split the word AT the accent (`transformaci-n`, `alcald-a`).
  // Decompose and drop the marks AFTER the Cyrillic pass, so `й`/`ё` (which NFD
  // also decomposes) are already Latin by the time this runs.
  if (foldAccents) s = s.normalize('NFD').replace(COMBINING_MARKS, '');
  return s;
}

/** The name half of the slug: transliterated, legal-form-stripped, hyphenated.
 *  `country` (optional) turns on that deployment's extra folding — Spain folds
 *  accents (`alcaldia`, not `alcald-a`); UZ, KZ and no country are unchanged. */
export function slugifyOrgName(name: string | null | undefined, country?: Country): string {
  if (!name) return '';
  const tokens = transliterate(name, foldsAccents(country))
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((tok) => tok.length > 0 && !STOPWORDS.has(tok));
  let slug = '';
  for (const tok of tokens) {
    const next = slug ? `${slug}-${tok}` : tok;
    if (next.length > MAX_SLUG_LEN) break;
    slug = next;
  }
  return slug;
}

/** Full buyer path slug segment: `{tin}-{name}`, or bare `{tin}` when the name
 *  slugifies to nothing (no registry name, or all stopwords). `country` as in
 *  {@link slugifyOrgName}. */
export function orgSlug(tin: string, name: string | null | undefined, country?: Country): string {
  const base = slugifyOrgName(name, country);
  return base ? `${tin}-${base}` : tin;
}

/**
 * A Spanish NIF at the start of a route param, as a whole token (followed by the
 * slug's `-` or the end): a CIF (legal entity — entity letter, 7 digits, control
 * digit or letter), a DNI-NIF (8 digits + letter), or a NIE / K-L-M NIF (letter,
 * 7 digits, letter). Case-insensitive, so a hand-typed lower-case URL still finds
 * the upper-case id the API stores. Same four shapes as `ES_NIF_SHAPE`.
 */
const ES_ORG_ID_PREFIX = /^([A-HJNP-SUVW]\d{7}[0-9A-J]|\d{8}[A-Z]|[KLMXYZ]\d{7}[A-Z])(?=-|$)/i;

/**
 * A Chilean RUT at the start of a route param, in its canonical spelling
 * (`76086428-5`, `61704000-K`), as a whole token: the dash INSIDE the RUT is part
 * of the id, so the leading digit run alone would cut the check digit off.
 * Case-insensitive (`61704000-k-codelco` still finds `61704000-K`).
 */
const CL_ORG_ID_PREFIX = /^([1-9]\d{0,7}-[0-9K])(?=-|$)/i;

/**
 * Recover the ИНН from a `/buyer/:seg` (or `/supplier/:seg`) route param that
 * may be slugged (`{tin}-{name}`) or bare (`{tin}`). The ИНН is the LEADING run
 * of digits — an org name never starts with the buyer's own ИНН, and the slug is
 * INN-first by construction. Returns the whole param when it has no leading
 * digits (defensive; never happens for a real buyer URL).
 *
 * Spain (`country === 'es'`): a NIF starts with a LETTER as often as not
 * (`Q3018001B-universidad-de-murcia`), so the digit run found nothing and the
 * whole slug went to the API — an empty profile on every slugged buyer URL.
 * There the NIF is matched first ({@link ES_ORG_ID_PREFIX}) and returned
 * upper-cased. Any other country (UZ, KZ — digits only) is unchanged.
 */
export function extractOrgTin(param: string, country?: Country): string {
  if (country === 'es') {
    const es = ES_ORG_ID_PREFIX.exec(param);
    if (es) return es[1].toUpperCase();
  }
  // Chile: the canonical RUT `digits-DV`, check digit included.
  if (country === 'cl') {
    const cl = CL_ORG_ID_PREFIX.exec(param);
    if (cl) return cl[1].toUpperCase();
  }
  const m = param.match(/^(\d+)/);
  return m ? m[1] : param;
}
